import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { useUser } from "../contexts/UserContext";
import { database } from "../constants/firebase";
import {
    ref,
    set,
    update,
    onValue,
    serverTimestamp
} from "firebase/database";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import { useParams } from "react-router";

const CreateVoucher = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser();
    const { id: voucherId } = useParams();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [code, setCode] = useState("");
    const [description, setDescription] = useState("");
    const [voucherType, setVoucherType] = useState("lifetime"); // lifetime | days
    const [days, setDays] = useState("");
    const [maxUses, setMaxUses] = useState(0);
    const [message, setMessage] = useState("");
    const [isActive, setIsActive] = useState(true);

    // ✅ EDIT MODE
    useEffect(() => {
        if (!voucherId) return;

        setIsEditing(true);

        const voucherRef = ref(database, `zylenvouchers/${voucherId}`);
        const unsubscribe = onValue(voucherRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();

                setCode(voucherId);
                setDescription(data.description || "");
                setMaxUses(data.maxUses || 0);
                setMessage(data.message || "");
                setIsActive(data.isActive ?? true);

                if (data.type === "days" && data.expiresAt) {
                    const now = new Date();
                    const expiry = new Date(data.expiresAt);

                    const diffDays = Math.ceil(
                        (expiry - now) / (1000 * 60 * 60 * 24)
                    );

                    setVoucherType("days");
                    setDays(diffDays > 0 ? diffDays : "");
                } else {
                    setVoucherType("lifetime");
                }
            }
        });

        return () => unsubscribe();
    }, [voucherId]);

    const handleSubmit = async () => {
        if (!code.trim() || !description.trim()) {
            alert("Please fill required fields");
            return;
        }

        if (voucherType === "days" && (!days || Number(days) <= 0)) {
            alert("Please enter valid number of days");
            return;
        }

        setIsSubmitting(true);

        try {
            let expiresAt = null;

            if (voucherType === "days") {
                const expiryDate = new Date();
                expiryDate.setDate(expiryDate.getDate() + Number(days));
                expiresAt = expiryDate.toISOString();
            }

            const voucherData = {
                description,
                type: voucherType,
                expiresAt,
                maxUses: Number(maxUses),
                message,
                isActive,
                updatedAt: serverTimestamp(),
            };

            if (isEditing) {
                await update(
                    ref(database, `zylenvouchers/${voucherId}`),
                    voucherData
                );
            } else {
                await set(
                    ref(database, `zylenvouchers/${code.toUpperCase()}`),
                    {
                        ...voucherData,
                        createdAt: serverTimestamp(),
                        usedCount: 0,
                        lastUsedAt: null,
                    }
                );
            }

            navigate(ROUTES.DASHBOARD);
        } catch (error) {
            console.error("Error saving voucher:", error);
            alert("Failed to save voucher");
        }

        setIsSubmitting(false);
    };

    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            <MobileHeader
                title={isEditing ? "Edit Voucher" : "Create Voucher"}
                isBackBtn={true}
            />

            <div className="flex">
                <Sidebar />

                <main className="flex-1 h-screen pt-10 pb-28 px-6 overflow-y-auto">
                    <div className="max-w-md mx-auto space-y-6">

                        {/* Voucher Code */}
                        <div>
                            <label className="block mb-2 text-sm">Voucher Code *</label>
                            <input
                                type="text"
                                value={code}
                                disabled={isEditing}
                                onChange={(e) =>
                                    setCode(e.target.value.toUpperCase())
                                }
                                placeholder="Enter voucher code..."
                                className="w-full px-4 py-3 rounded-xl bg-[#F5F9FE] dark:bg-white text-black dark:text-[#262626] border border-gray-300"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block mb-2 text-sm">Description *</label>
                            <input
                                type="text"
                                value={description}
                                onChange={(e) =>
                                    setDescription(e.target.value)
                                }
                                placeholder="Voucher description..."
                                className="w-full px-4 py-3 rounded-xl bg-[#F5F9FE] dark:bg-white text-black dark:text-[#262626] border border-gray-300"
                            />
                        </div>

                        {/* Voucher Type */}
                        <div>
                            <label className="block mb-3 text-sm font-medium">
                                Voucher Type
                            </label>

                            <div className="flex gap-6 items-center">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="voucherType"
                                        value="lifetime"
                                        checked={
                                            voucherType === "lifetime"
                                        }
                                        onChange={() =>
                                            setVoucherType("lifetime")
                                        }
                                    />
                                    Lifetime
                                </label>

                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="voucherType"
                                        value="days"
                                        checked={voucherType === "days"}
                                        onChange={() =>
                                            setVoucherType("days")
                                        }
                                    />
                                    Days
                                </label>
                            </div>

                            {voucherType === "days" && (
                                <input
                                    type="number"
                                    placeholder="Enter number of days..."
                                    value={days}
                                    onChange={(e) =>
                                        setDays(e.target.value)
                                    }
                                    className="mt-4 w-full px-4 py-3 rounded-xl bg-[#F5F9FE] dark:bg-white text-black dark:text-[#262626] border border-gray-300"
                                />
                            )}
                        </div>

                        {/* Max Uses */}
                        <div>
                            <label className="block mb-2 text-sm">
                                Max Uses (0 = Unlimited)
                            </label>
                            <input
                                type="number"
                                value={maxUses}
                                onChange={(e) =>
                                    setMaxUses(e.target.value)
                                }
                                className="w-full px-4 py-3 rounded-xl bg-[#F5F9FE] dark:bg-white text-black dark:text-[#262626] border border-gray-300"
                            />
                        </div>

                        {/* Success Message */}
                        <div>
                            <label className="block mb-2 text-sm">
                                Success Message
                            </label>
                            <textarea
                                value={message}
                                onChange={(e) =>
                                    setMessage(e.target.value)
                                }
                                placeholder="Message shown after redeem..."
                                rows={3}
                                className="w-full px-4 py-3 rounded-xl bg-[#F5F9FE] dark:bg-white text-black dark:text-[#262626] border border-gray-300"
                            />
                        </div>

                        {/* Active Toggle */}
                        <div className="flex items-center justify-between">
                            <span>Active</span>
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={() =>
                                    setIsActive(!isActive)
                                }
                                className="w-5 h-5"
                            />
                        </div>

                        {/* Submit */}
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="bg-primary text-white text-lg font-semibold py-3 rounded-xl w-full"
                        >
                            {isSubmitting
                                ? "Saving..."
                                : isEditing
                                    ? "Update Voucher"
                                    : "Create Voucher"}
                        </button>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default CreateVoucher;
